import { z } from "zod";

/**
 * Domain entities as they cross the API boundary (camelCase JSON).
 * These mirror docs/spec.allium. Timestamps are epoch milliseconds.
 */

export const readState = z.enum(["unread", "read"]);
export type ReadState = z.infer<typeof readState>;

export const conditionField = z.enum(["title", "content", "author"]);
export type ConditionField = z.infer<typeof conditionField>;

export const conditionOperator = z.enum(["contains", "matches"]);
export type ConditionOperator = z.infer<typeof conditionOperator>;

export const conditionMatch = z.enum(["match_all", "match_any"]);
export type ConditionMatch = z.infer<typeof conditionMatch>;

/** Extensible trigger discriminator; only new_article exists today. */
export const ruleTrigger = z.enum(["new_article"]);
export type RuleTrigger = z.infer<typeof ruleTrigger>;

/** Extensible action discriminator. */
export const ruleActionKind = z.enum(["mark_read", "keep"]);
export type RuleActionKind = z.infer<typeof ruleActionKind>;

export const ruleOutcome = z.enum(["succeeded", "failed"]);
export type RuleOutcome = z.infer<typeof ruleOutcome>;

export const ruleCondition = z.object({
  field: conditionField,
  operator: conditionOperator,
  query: z.string().min(1),
});
export type RuleCondition = z.infer<typeof ruleCondition>;

export const ruleAction = z.object({
  action: ruleActionKind,
});
export type RuleAction = z.infer<typeof ruleAction>;

export const feed = z.object({
  id: z.string(),
  url: z.string().url(),
  title: z.string().nullable(),
  siteUrl: z.string().nullable(),
  errorCount: z.number().int().nonnegative(),
  lastFetchedAt: z.number().int().nullable(),
  /** error_count > 0 */
  isErrored: z.boolean(),
  /** error_count >= feed_unhealthy_threshold */
  appearsDead: z.boolean(),
  /** Same-origin path to the feed's stored favicon, or null to render a letter avatar. */
  faviconUrl: z.string().nullable(),
});
export type Feed = z.infer<typeof feed>;

export const feedErrorKind = z.enum(["fetch", "parse", "ingest"]);
export type FeedErrorKind = z.infer<typeof feedErrorKind>;

export const feedError = z.object({
  id: z.string(),
  feedUrl: z.string(),
  kind: feedErrorKind,
  /** HTTP status for fetch failures; null for parse/ingest and network errors. */
  status: z.number().int().positive().nullable(),
  message: z.string(),
  occurredAt: z.number().int(),
});
export type FeedError = z.infer<typeof feedError>;

export const article = z.object({
  id: z.string(),
  feedUrl: z.string(),
  feedTitle: z.string(),
  itemId: z.string(),
  title: z.string(),
  content: z.string(),
  author: z.string().nullable(),
  link: z.string(),
  publishedAt: z.number().int().nullable(),
  fetchedAt: z.number().int(),
  readState,
  kept: z.boolean(),
  updated: z.boolean(),
  /** publishedAt ?? fetchedAt */
  effectiveDate: z.number().int(),
  /** Own copy of the feed's favicon path (like feed_title), so the mixed stream can render it. */
  feedFaviconUrl: z.string().nullable(),
});
export type Article = z.infer<typeof article>;

export const rule = z.object({
  id: z.string(),
  name: z.string().min(1),
  enabled: z.boolean(),
  trigger: ruleTrigger,
  /** null = all feeds */
  watchedFeedUrl: z.string().nullable(),
  conditionMatch,
  conditions: z.array(ruleCondition),
  actions: z.array(ruleAction).min(1),
});
export type Rule = z.infer<typeof rule>;

export const ruleEvent = z.object({
  id: z.string(),
  ruleId: z.string(),
  ruleName: z.string(),
  occurredAt: z.number().int(),
  articleId: z.string().nullable(),
  feedUrl: z.string().nullable(),
  matchedConditions: z.boolean(),
  actionsTaken: z.array(ruleAction),
  outcome: ruleOutcome,
  detail: z.string().nullable(),
});
export type RuleEvent = z.infer<typeof ruleEvent>;

/** How a discovered feed candidate was found. Mirrors the extension's discovery. */
export const feedCandidateSource = z.enum([
  "advertised",
  "common-path",
  "self",
]);
export type FeedCandidateSource = z.infer<typeof feedCandidateSource>;

/** One feed the discovery endpoint offers to subscribe. */
export const feedCandidate = z.object({
  url: z.string().url(),
  title: z.string().nullable(),
  source: feedCandidateSource,
});
export type FeedCandidate = z.infer<typeof feedCandidate>;
